/**
 * API Service Client for Lamma Platform
 */

import axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import toast from 'react-hot-toast';
import { deliverChanges, isChangeBatch } from '@/core/changes/router';
import {
  enqueueModeSwitch,
  type ModeFitRequestConfig,
  type SwitchTarget,
} from '@/modules/auth/services/mode_fit_queue';

// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
export const API_VERSION = 'v1';

// WebSocket Configuration
// Converts http -> ws, https -> wss
export const WS_BASE_URL = API_BASE_URL.replace(/^http/, 'ws');

const ENTITY_CHANGES_HEADER = 'X-Entity-Changes';

function deliverEntityChangesHeader(response: AxiosResponse | undefined): void {
  if (!(response?.headers instanceof AxiosHeaders)) return;
  const value = response.headers.get(ENTITY_CHANGES_HEADER);
  if (typeof value !== 'string') return;
  try {
    const batch: unknown = JSON.parse(value);
    if (isChangeBatch(batch)) deliverChanges(batch);
  } catch {
    console.warn('Malformed entity changes header');
  }
}

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// ANONYMOUS API CLIENT (for anonymous user requests)
// ============================================

/**
 * Create a separate axios instance for anonymous user requests
 * that doesn't require authentication and won't redirect to login on 401
 */
export const anonymousClient = axios.create({
  baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token if available (for logged-in users)
anonymousClient.interceptors.request.use(
  (config) => {
    // Try to get auth token from apiClient's default headers OR from the stored token
    const authHeader = apiClient.defaults.headers.common['Authorization'];
    if (authHeader && typeof authHeader === 'string') {
      config.headers['Authorization'] = authHeader;
    } else if (authToken) {
      // Fallback to stored token if header not set yet
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor that doesn't redirect on 401 (allows anonymous access)
anonymousClient.interceptors.response.use(
  (response) => {
    deliverEntityChangesHeader(response);
    return response;
  },
  (error) => {
    deliverEntityChangesHeader(error.response);
    // Just reject without redirecting - let the component handle errors
    return Promise.reject(error);
  }
);

// Token storage
let authToken: string | null = null;

// Token getter function - will be set by useAuth hook
// This allows the API client to fetch fresh tokens from Clerk
type TokenGetter = () => Promise<string | null>;
let tokenGetter: TokenGetter | null = null;

// Function to set the token getter (called once by useAuth)
export const setTokenGetter = (getter: TokenGetter) => {
  tokenGetter = getter;
};

export type AuthLostHandler = () => void;

const sendToLogin: AuthLostHandler = () => {
  window.location.href = '/login';
};

let authLostHandler: AuthLostHandler = sendToLogin;

/** What a lost login does; null restores the default (a full load of /login). */
export const setAuthLostHandler = (handler: AuthLostHandler | null) => {
  authLostHandler = handler ?? sendToLogin;
};

// Auth-token-ready signal. Resolves the first time setAuthToken receives a
// non-null token. Modules that depend on authenticated requests (notably
// the element manifest registry) await this before firing their initial
// GET, eliminating the 401 retry storm that otherwise occurs while Clerk
// completes auth handshake.
let resolveTokenReady: (() => void) | null = null;
const tokenReadyPromise: Promise<void> = new Promise((resolve) => {
  resolveTokenReady = resolve;
});
let tokenReady = false;

export const awaitAuthToken = (): Promise<void> => tokenReadyPromise;

// Function to set the auth token
export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    if (!tokenReady && resolveTokenReady) {
      tokenReady = true;
      resolveTokenReady();
      resolveTokenReady = null;
    }
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    if (authToken && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => {
    deliverEntityChangesHeader(response);
    return response;
  },
  async (error: AxiosError) => {
    deliverEntityChangesHeader(error.response);
    // Check if error toasts are suppressed for this request
    const config = error.config as any;
    const suppressErrorToast = config?.suppressErrorToast;

    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data as any;

      switch (status) {
        case 401:
          // Anonymous users (no tokenGetter) are allowed endpoints that support optional auth
          if (!tokenGetter) break;
          // Try to refresh the token and retry the request once
          if (!config._retry) {
            config._retry = true;
            let freshToken: string | null;
            try {
              freshToken = await tokenGetter();
            } catch (refreshError) {
              // A refresh that could not reach the server is not a lost login: keep the user signed in
              console.error('Token refresh failed:', refreshError);
              return Promise.reject(error);
            }
            if (freshToken) {
              setAuthToken(freshToken);
              config.headers['Authorization'] = `Bearer ${freshToken}`;
              return apiClient.request(config);
            }
          }
          console.error('Authentication error - login lost:', data);
          setAuthToken(null);
          authLostHandler();
          break;

        case 402: {
          // Payment Required — insufficient credits
          // Backend HTTPException goes through http_exception_handler which puts
          // the detail dict under data.error (not data.detail).
          const creditDetail = data?.detail?.error === 'insufficient_credits'
            ? data.detail
            : typeof data?.error === 'object' && data.error?.error === 'insufficient_credits'
              ? data.error
              : null;
          if (creditDetail) {
            window.dispatchEvent(new CustomEvent('insufficient-credits', { detail: creditDetail }));
          }
          break;
        }

        case 403: {
          // Mode-fit branch: when the backend signals that the request would
          // succeed under a different context (mode_switch_available) or is
          // blocked because no capable mode exists (no_capable_mode), park the
          // request on the mode-fit queue and let ModeSwitchModal drive the
          // resolution. This runs regardless of suppressErrorToast — a
          // recoverable mode switch is never silent.
          //
          // Backend HTTPException goes through http_exception_handler which
          // wraps the original detail dict under data.error (not data.detail),
          // mirroring the 402 path above.
          const modeFitDetail =
            (typeof data?.error === 'object' ? data.error : null) ??
            (typeof data?.detail === 'object' ? data.detail : null);
          const code = modeFitDetail?.code;
          const modeFitConfig = config as ModeFitRequestConfig;
          if (code === 'mode_switch_available' || code === 'no_capable_mode') {
            // Already a retry: surface unchanged so callers see the second
            // response instead of triggering another modal.
            if (modeFitConfig._modeFitRetry) {
              break;
            }
            // Defensive reads: a malformed mode-fit response (missing
            // context) must not throw inside the interceptor. Guard with
            // optional chaining and fall through to the generic
            // permission-denied flow when neither target nor reasonKey is
            // present.
            const ctx = modeFitDetail?.context;
            const target =
              code === 'mode_switch_available'
                ? ((ctx?.target as SwitchTarget | undefined) ?? null)
                : null;
            const reasonKey =
              code === 'no_capable_mode'
                ? ((ctx?.reason_key as string | undefined) ?? null)
                : null;
            if (target === null && reasonKey === null) {
              break;
            }
            return new Promise((resolve, reject) => {
              enqueueModeSwitch({
                config: modeFitConfig,
                resolve,
                reject,
                target,
                reasonKey,
              });
            });
          }

          // Forbidden — dispatch custom event for the global PermissionDeniedModal.
          // The modal shows contextual messaging (plan vs org vs seat restriction)
          // instead of a raw error toast. Mirrors the 402 pattern above.
          console.error('Permission error:', data);
          if (!suppressErrorToast) {
            const detail = typeof data?.detail === 'object'
              ? data.detail
              : { message: data?.detail || data?.error || "You don't have permission to perform this action" };
            window.dispatchEvent(new CustomEvent('permission-denied', { detail }));
          }
          break;
        }

        case 404:
          // Not found
          console.error('Resource not found:', data);
          if (!suppressErrorToast) {
            toast.error(data?.detail || data?.error || 'Resource not found');
          }
          break;

        case 410:
          // CONTENT_DELETED — the referenced published content has been
          // hard-deleted by the author. The caller will refetch and
          // re-render via the row's content_deleted_at field, which the
          // backend keeps populated. See docs/delivery-engine/content-lifecycle.md.
          console.warn('Content deleted:', data);
          if (!suppressErrorToast) {
            const message =
              (typeof data?.detail === 'string' && data.detail) ||
              data?.error ||
              'This content is no longer available.';
            toast.error(message);
          }
          break;

        case 422: {
          // Validation error
          console.error('Validation error:', data);
          if (!suppressErrorToast) {
            const detailMessage =
              typeof data?.detail === 'string' ? data.detail : data?.detail?.message;
            toast.error(detailMessage || data?.error || 'Please check your input and try again');
          }
          break;
        }

        case 500:
          // Server error
          console.error('Server error:', data);
          if (!suppressErrorToast) {
            toast.error('Something went wrong on our end. Please try again later.');
          }
          break;

        default:
          console.error('API error:', data);
          if (!suppressErrorToast) {
            toast.error(data?.detail || data?.error || 'An unexpected error occurred');
          }
      }
    } else if (error.request) {
      // Request made but no response
      console.error('Network error:', error.message);
      if (!suppressErrorToast) {
        toast.error('Network error. Please check your connection and try again.');
      }
    } else {
      // Something else happened
      console.error('Request error:', error.message);
      if (!suppressErrorToast) {
        toast.error('Failed to make request. Please try again.');
      }
    }
    
    return Promise.reject(error);
  }
);

// API Service functions
export const apiService = {
  // Health checks
  health: () => apiClient.get('/health'),
  
  // Authentication
  auth: {
    me: () => apiClient.get('/auth/me'),
    status: () => apiClient.get('/auth/status'),
    updateProfile: (data: any) => apiClient.post('/auth/update-profile', data),
  },

  // Onboarding API
  onboarding: {
    // Fetch role-specific field options (grade levels, subjects)
    getFieldOptions: (role: string) =>
      apiClient.get('/onboarding/field-options', { params: { role } }),

    // Complete onboarding with unified endpoint
    complete: (data: {
      role: string;
      language: string;
      date_of_birth: string;
      country: string;
      region?: string | null;
      school?: string | null;
      grade_levels: string[];
      subjects: string[];
      display_name?: string | null;
    }) => apiClient.post('/onboarding/complete', data),

    resetUser: (userId: string) => apiClient.post(`/onboarding/reset/${userId}`),
  },
  
  // Users
  users: {
    profile: () => apiClient.get('/users/profile'),
    dashboard: () => apiClient.get('/users/dashboard-data'),
  },
  
  // Generic CRUD operations
  get: (url: string, config?: any) => apiClient.get(url, config),
  post: (url: string, data?: any, config?: any) => apiClient.post(url, data, config),
  put: (url: string, data?: any, config?: any) => apiClient.put(url, data, config),
  delete: (url: string, config?: any) => apiClient.delete(url, config),
  patch: (url: string, data?: any, config?: any) => apiClient.patch(url, data, config),
};

// ============================================
// HELPER FUNCTIONS FOR ANONYMOUS SUPPORT
// ============================================

/**
 * Get the appropriate API client based on whether user is anonymous
 *
 * @param isAnonymous - Whether the request is for an anonymous user
 * @returns The appropriate axios instance
 */
export const getClientForContext = (isAnonymous: boolean): AxiosInstance => {
  return isAnonymous ? anonymousClient : apiClient;
};

/**
 * Add X-Participant-ID header to request config for anonymous users
 *
 * @param config - Axios request config (optional)
 * @param participantId - The participant ID to add to headers
 * @returns Updated config with participant header
 */
export const addParticipantIdHeader = (
  config: AxiosRequestConfig = {},
  participantId: string | null | undefined
): AxiosRequestConfig => {
  if (!participantId) {
    return config;
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      'X-Participant-ID': participantId,
    },
  };
};

/**
 * Add X-Session-Token header for public assignment anonymous users.
 *
 * Session tokens identify anonymous users who joined via a public assignment.
 * This is distinct from X-Participant-ID (live session sync) — both can coexist
 * when a public assignment is delivered via a live session.
 */
export const addSessionTokenHeader = (
  config: AxiosRequestConfig = {},
  sessionToken: string | null | undefined
): AxiosRequestConfig => {
  if (!sessionToken) {
    return config;
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      'X-Session-Token': sessionToken,
    },
  };
};

export interface AnonymousContext {
  participantId?: string | null;
  sessionToken?: string | null;
}

export const buildAnonymousConfig = (
  baseConfig: AxiosRequestConfig,
  ctx: AnonymousContext
): AxiosRequestConfig => {
  let config = baseConfig;
  config = addParticipantIdHeader(config, ctx.participantId);
  config = addSessionTokenHeader(config, ctx.sessionToken);
  return config;
};

export default apiService;